/**
 * @file: order.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { HttpClient } from '@euler/lib/request';
import {
  CreateChildOrderRequest,
  CreateChildOrderResponse,
  CreateOrderRequest,
  OrderVoiceUrls,
  ReportContentType,
  ReportShareMethod,
  ServiceOrderInfo,
  UpdateOrderRequest,
} from '@euler/model';
import { LatestOrderInfo } from '@euler/model/LatestOrderInfo';
import { ReportShareViewSummary } from '@euler/model/ReportShareViewSummary';
import { ReportViewSummary } from '@euler/model/ReportViewSummary';
import { CursorResult, IdType } from '@euler/model/common';
import {
  VehicleInspectionTask,
  VehicleReceptionOrder,
} from '@euler/model/entity';
import { ReportShareTarget } from '@euler/model/enum/ReportShareTarget';
import { SearchOrderParams } from '@euler/model/order/SearchOrderParams';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { getCommonRequestParametersAsSearchQuery } from '@euler/services/utils';

export class OrderService {
  constructor(private readonly api: HttpClient) {}

  async searchOrders(
    params: SearchOrderParams,
    nextKey: IdType | undefined,
    limit: number,
  ): Promise<CursorResult<SearchOrderResultItem, IdType>> {
    return await this.api
      .post()
      .url('/orders/search')
      .data({ params, nextKey, limit })
      .future();
  }

  async getLatestOrderInfo(args: {
    vin?: string;
    licensePlateNo?: string;
  }): Promise<LatestOrderInfo | null> {
    return await this.api.get().url('/orders/most-recent-order', args).future();
  }

  async plateOrder(request: ServiceOrderInfo): Promise<string> {
    return await this.api.post().url('/orders').data(request).future();
  }

  async createOrderV2(
    request: CreateOrderRequest,
    args?: { __fake__?: boolean; __simulatefailure__?: boolean },
  ): Promise<{ task: VehicleInspectionTask; order: VehicleReceptionOrder }> {
    return await this.api
      .post()
      .url('/orders/v2', args)
      .data({ request })
      .future();
  }

  async createChildOrder(
    mainOrderNo: string,
    request: CreateChildOrderRequest,
    args?: { __fake__?: boolean; __simulatefailure__?: boolean },
  ): Promise<CreateChildOrderResponse> {
    return await this.api
      .post()
      .url('/orders/:orderNo/child-orders', { orderNo: mainOrderNo, ...args })
      .data({ request })
      .future();
  }

  async updateOrder(
    orderNo: string,
    request: UpdateOrderRequest,
  ): Promise<VehicleReceptionOrder> {
    return await this.api
      .patch()
      .url('/orders/:orderNo', { orderNo })
      .data({ request })
      .future();
  }

  async cancelOrder(
    orderNo: string,
    params: { reason: string },
  ): Promise<void> {
    await this.api
      .post()
      .url('/orders/:orderNo/cancel', { orderNo })
      .data(params)
      .future();
  }

  async getRenderOrderAsPdfUrl(orderNo: string): Promise<string> {
    const commonSearchQuery = await getCommonRequestParametersAsSearchQuery();
    return this.api.url(`/orders/${orderNo}.pdf`, {
      ...commonSearchQuery,
    });
  }

  async getRenderQuotationFormAsPdfUrl(
    orderNo: string,
    options: {
      type: 'quotation-form' | 'settlement-form';
      showPrice?: boolean;
    },
  ): Promise<string> {
    const commonSearchQuery = await getCommonRequestParametersAsSearchQuery();
    return this.api.url(`/orders/${orderNo}/quotation-form.pdf`, {
      ...commonSearchQuery,
      ...options,
    });
  }

  async getSubscribeBindQrcodeImageUrl(args?: {
    lpn: string;
    logo?: boolean;
    size?: number;
    __fake__?: boolean;
  }): Promise<string> {
    const commonSearchQuery = await getCommonRequestParametersAsSearchQuery();
    return this.api.url('orders/vehicle-subscription/qrcode.png', {
      ...args,
      ...commonSearchQuery,
    });
  }

  async getReportViewSummary(orderNo: string): Promise<ReportViewSummary> {
    return await this.api
      .get()
      .url('/orders/:orderNo/report-view-summary', { orderNo })
      .future();
  }

  async getReportShareViewSummary(
    orderNo: string,
    args?: {
      withViewSummary?: boolean;
      withShareSummary?: boolean;
    },
  ): Promise<ReportShareViewSummary> {
    return await this.api
      .get()
      .url('/orders/:orderNo/share-view-summary', {
        orderNo,
        with_view_summary: args?.withViewSummary,
        with_share_summary: args?.withShareSummary,
      })
      .future();
  }

  async getOrderVoiceUrls(orderNo: string): Promise<OrderVoiceUrls | null> {
    return await this.api
      .get()
      .url('/orders/:orderNo/voice-urls', { orderNo })
      .future();
  }

  async canRollbackFinished(orderNo: string): Promise<boolean> {
    return await this.api
      .get()
      .url('/orders/:orderNo/can-rollback-finished', { orderNo })
      .future();
  }

  async rollbackFinished(
    orderNo: string,
    args: {
      reason: string;
    },
  ): Promise<boolean> {
    return await this.api
      .post()
      .url('/orders/:orderNo/rollback-finished', { orderNo })
      .data(args)
      .future();
  }

  async logReportSharing(
    orderNo: string,
    args: {
      reportType: ReportContentType;
      shareMethod: ReportShareMethod;
      shareTarget?: ReportShareTarget;
    },
  ) {
    return await this.api
      .post()
      .url('/orders/:orderNo/report-share-logs', { orderNo })
      .data(args)
      .future();
  }
}
